import { Navigate, RouterProvider, createBrowserRouter, Outlet } from 'react-router-dom';
import { useAuthStore } from '../lib/auth';
import { useSession } from '../lib/useSession';
import SignInPage from '../pages/SignInPage';
import PacksListPage from '../pages/PacksListPage';
import NewPackPage from '../pages/NewPackPage';
import PackEditorPage from '../pages/PackEditorPage';
import NetworkBanner from '../components/NetworkBanner';
import { Toaster } from '../components/Toast';
import ReauthModal from '../components/ReauthModal';

/**
 * Root route resolver.
 *
 * Sends unauthenticated visitors to `/sign-in` and authenticated visitors to
 * `/packs`. The actual cookie validation lives in `functions/admin/v1/_session.ts`
 * (presence check) and the upstream Worker (signature + expiry); the SPA only
 * mirrors that decision in-memory.
 */
function RootRedirect(): JSX.Element {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return <Navigate to={isAuthenticated ? '/packs' : '/sign-in'} replace />;
}

/**
 * Route guard for every authenticated path.
 *
 * Validates Requirement 20.1: any path other than `/sign-in` without a valid
 * auth cookie redirects to `/sign-in`. Cookie presence is checked at boot via
 * `useSession`; the upstream Worker validates the JWT on the next admin call
 * and returns 401 if expired, which is handled by `<ReauthModal>`.
 */
function RequireAuth(): JSX.Element {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) {
    return <Navigate to="/sign-in" replace />;
  }
  return <Outlet />;
}

const router = createBrowserRouter([
  { path: '/', element: <RootRedirect /> },
  { path: '/sign-in', element: <SignInPage /> },
  {
    element: <RequireAuth />,
    children: [
      { path: '/packs', element: <PacksListPage /> },
      { path: '/packs/new', element: <NewPackPage /> },
      { path: '/packs/:id', element: <PackEditorPage /> },
    ],
  },
  { path: '*', element: <RootRedirect /> },
]);

export default function App(): JSX.Element {
  // Boot-time cookie-presence check; flips `isAuthenticated` so the route
  // guard renders the right shell on first paint.
  useSession();
  return (
    <>
      {/* Global UX layer mounted before the router so toasts, the offline
          banner, and the re-auth modal stay visible across route changes
          (Requirements 20.4, 25.1, 25.2). */}
      <NetworkBanner />
      <Toaster />
      <ReauthModal />
      <RouterProvider router={router} />
    </>
  );
}
